/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import { waitFor } from '@ember/test-waiters';
import type { UploadFile } from 'ember-file-upload';

export interface MultiFileUploadResult {
  s3Url: string;
  fileName: string;
  fileType: string;
  fileSize: number;
}

interface IArgs {
  queueName?: string;
  acceptedTypes: string[];
  maxFileSize: number;
  title: string;
  description: string;
  chooseFileLabel: string;
  supportedTypesLabel: string;
  uploadingLabel: string;
  invalidFileErrorKey?: string;
  uploadFailedErrorKey?: string;
  onFileUploaded(result: MultiFileUploadResult): void;
}

interface MultiFileUploadSignature {
  Element: HTMLElement;
  Args: IArgs;
}

export default class UiMultiFileUploadComponent extends Component<MultiFileUploadSignature> {
  @service fileHandler!: Services['file-handler'];
  @service sentry!: Services['sentry'];
  @service notifications!: Services['notifications'];

  @tracked isUploading = false;

  get queueName(): string {
    return this.args.queueName ?? 'multi-file-upload';
  }

  get acceptedTypesString(): string {
    return this.args.acceptedTypes.join(', ');
  }

  @action
  @waitFor
  async handleFileAdded(file: UploadFile): Promise<void> {
    if (!this.#isFileValid(file.file)) {
      this.notifications.error(
        this.args.invalidFileErrorKey ?? 'errors.invalid_file',
      );

      return;
    }

    this.isUploading = true;

    try {
      const { s3Url } = await this.fileHandler.uploadFile(file);

      this.args.onFileUploaded({
        s3Url,
        fileName: file.file.name,
        fileType: file.file.type,
        fileSize: file.file.size,
      });
    } catch (error) {
      this.sentry.captureException(error);
      this.notifications.error(
        this.args.uploadFailedErrorKey ?? 'errors.upload_failed',
      );
    } finally {
      this.isUploading = false;
    }
  }

  #isFileValid(file: File): boolean {
    return (
      this.args.acceptedTypes.includes(file.type) &&
      file.size <= this.args.maxFileSize
    );
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Ui::MultiFileUpload': typeof UiMultiFileUploadComponent;
  }
}
